<script setup>
import { StatusEnum } from "@/utilities/enum";
import { capitalized, formatStringToNumber } from "@/utilities/helpers";
import { typeEquipmentEnum } from "../core/FormData";
</script>

<template>
  <div class="flex items-center justify-between">
    <h3 class="font-medium text-lg text-gray-800">Unit Hauling</h3>
  </div>
  <hr class="border-t-2 border-dashed border-gray-500" />

  <div class="grid grid-cols-2 gap-3 items-center">
    <v-text-field
      variant="outlined"
      color="#475569"
      density="compact"
      hide-details
      clearable
      prepend-inner-icon="mdi-magnify"
      placeholder="Cari jenis peralatan"
      v-model="keyword"
      @keyup.enter="searchData()"
    >
    </v-text-field>
    <div class="flex justify-end">
      <v-btn
        class="capitalize text-xs min-w-40 w-40 bg-gray-800"
        @click="addRow(typeEquipmentEnum.BERGERAK)"
        v-if="
          (!status ||
            status === StatusEnum.DRAFT ||
            status === StatusEnum.REVISION ||
            (status === StatusEnum.COMPLETED && isAdmin)) &&
          !isPjo
        "
      >
        <span class="text-white text-sm">Tambah List</span>
        <template v-slot:prepend>
          <img
            src="../../../assets/icon/add-square.png"
            alt="icon"
            class="w-5 h-auto"
            loading="lazy"
          />
        </template>
      </v-btn>
    </div>
  </div>
  <div class="w-full">
    <v-data-table-server
      :items-per-page="existing.perPage"
      :headers="
        localDataTable.headers.filter((item, index, array) => {
          if (
            (status === StatusEnum.COMPLETED && !isAdmin) ||
            status === StatusEnum.WAITING_APPROVAL
          ) {
            if (index === array.length - 1) {
              return item.title !== '';
            }
          }
          return true;
        })
      "
      :items="localDataTable.items"
      :loading="existing.loading"
      :items-length="existing.totalItems"
      :page="existing.page"
      @update:options="fetchData"
    >
      <template #thead>
        <thead class="summary">
          <tr class="h-14 border-y">
            <td></td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ sumUnit() }}
                </h5>
              </div>
            </td>
            <td>
              <div
                class="w-full h-10 rounded-md bg-blue-100 flex justify-center items-center"
              >
                <h5 class="font-medium text-black text-sm">
                  {{ countEquipment() }}
                </h5>
              </div>
            </td>
            <td
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            ></td>
          </tr>
        </thead>
      </template>
      <template v-slot:item="{ item, index }">
        <tr class="border-gray-950 h-14 align-top" v-if="!item.deleted">
          <td class="pt-4 font-medium">
            <p>{{ getIndex(index)?.displayIndex }}</p>
          </td>
          <td class="py-2">
            <v-text-field
              :key="item.uuid"
              variant="outlined"
              color="#475569"
              density="compact"
              placeholder="ex: 100"
              class="w-full"
              @input="handleInput($event, 'unit', index)"
              v-model="item.unit"
              :id="'unit_' + getIndex(index)?.displayIndex"
              :rules="formValidasi.unit"
              autocomplete="off"
              hide-details="auto"
              @blur="unit_touched = true"
              @update:model-value="item.changed = true"
              :error="hasErrors(item, 'unit')"
              :error-messages="getErrors(item, 'unit')"
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            ></v-text-field>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ item.unit }}
            </h6>
          </td>
          <td class="py-2">
            <v-tooltip
              :text="item.equipment?.name ?? null"
              location="top"
              :disabled="
                !item.equipment?.name || item.equipment?.name?.trim() === ''
              "
              v-if="
                (!status ||
                  status === StatusEnum.DRAFT ||
                  status === StatusEnum.REVISION ||
                  (status === StatusEnum.COMPLETED && isAdmin)) &&
                !isPjo
              "
            >
              <template v-slot:activator="{ props }">
                <v-autocomplete
                  :key="item.uuid"
                  v-bind="item.equipment?.name ? props : {}"
                  class="w-full"
                  variant="outlined"
                  color="#475569"
                  rounded="xs"
                  density="compact"
                  placeholder="Pilih Peralatan"
                  :items="
                    listEquipment.find((data) => data?.uuid === item?.uuid)
                      ?.value
                  "
                  item-title="title"
                  item-value="value"
                  v-model="item.equipment"
                  @input="
                    (event) => {
                      getEquipment(
                        event.target.value,
                        typeEquipmentEnum.BERGERAK,
                        item.uuid
                      );
                      item.equipment = null;
                    }
                  "
                  :id="'equipment_' + getIndex(index)?.displayIndex"
                  :rules="formValidasi.equipment"
                  @blur="item.equipment_touched = true"
                  @update:model-value="item.changed = true"
                  :error-messages="
                    existing.items.find((data) => data.uuid === item.uuid)
                      ?.duplicate
                      ? 'Peralatan tidak boleh duplicate.'
                      : getErrors(item, 'equipment')
                  "
                  autocomplete="off"
                  hide-details="auto"
                  :error="hasErrors(item, 'equipment')"
                >
                </v-autocomplete>
              </template>
            </v-tooltip>
            <h6 v-else class="font-normal text-sm mt-2">
              {{ item.name }}
            </h6>
          </td>
          <td
            class="text-center pt-4"
            v-if="
              (!status ||
                status === StatusEnum.DRAFT ||
                status === StatusEnum.REVISION ||
                (status === StatusEnum.COMPLETED && isAdmin)) &&
              !isPjo
            "
          >
            <img
              src="../../../assets/icon/trash.png"
              alt="icon"
              loading="lazy"
              class="min-w-6 w-6 cursor-pointer h-auto"
              @click="
                removeRow(item.existing_index, typeEquipmentEnum.BERGERAK)
              "
              :id="'remove_' + getIndex(index)?.displayIndex"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </div>
</template>

<script>
export default {
  name: "ContentEquipmentComponent",
  data() {
    return {
      keyword: null,
    };
  },
  props: {
    dataTable: Object,
    existing: Object,
    formValidasi: Object,
    listEquipment: Array,
    getEquipment: Function,
    handleInput: Function,
    addRow: Function,
    removeRow: Function,
    setTabActive: Function,
    isPjo: Boolean,
    status: String,
    isAdmin: Boolean,
  },
  computed: {
    localDataTable: {
      get() {
        return this.dataTable;
      },
      set(newValue) {
        this.$emit("update:dataTable", newValue); // Kirim event ke parent
      },
    },
    indexedData() {
      let counter = 1;
      let startIndex = this.dataTable.start_index ?? 0;
      return this.dataTable.items.map((dt, index) => {
        if (!dt.deleted) {
          return {
            ...dt,
            displayIndex: startIndex + counter++,
            realIndex: index,
          };
        } else {
          return { ...dt, displayIndex: null, realIndex: index };
        }
      });
    },
  },
  methods: {
    capitalized,
    async fetchData({ page, itemsPerPage } = {}) {
      this.$emit("fetch-data", page, itemsPerPage, typeEquipmentEnum.BERGERAK);
    },
    async searchData() {
      this.$emit("search-data", this.keyword, typeEquipmentEnum.BERGERAK);
    },
    getIndex(index) {
      return this.indexedData.find((item) => item.realIndex === index);
    },
    sumUnit() {
      let total = 0;
      total = this.existing.items
        .filter((item) => !item.deleted && item.unit)
        .map((data) => {
          const num = formatStringToNumber(data.unit);
          return isNaN(num) ? 0 : num;
        })
        .reduce((sum, num) => sum + num, 0);
      return total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    countEquipment() {
      return new Set(
        this.existing.items
          .filter((item) => !item.deleted && item.equipment)
          .map((item) => item.equipment.id)
      ).size;
    },
    hasErrors(data, touched) {
      return this.getErrors(data, touched).length > 0;
    },
    getErrors(value, touched) {
      if (!value[`${touched}_touched`]) return [];
      const rules = this.formValidasi[touched];
      const errors = rules
        .map((rule) => rule(value[touched]))
        .filter((result) => result !== true);
      return errors;
    },
  },
  emits: ["fetch-data", "search-data"],
};
</script>

<style scoped>
:deep(.v-table > .v-table__wrapper > table > tbody > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > td),
:deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
  padding: 0px 5px;
}
</style>
